import EventEmitter from "@repo/utils/EventEmitter";
import Log from "@repo/utils/Log";
import UObject from "@repo/utils/UObject";
import UPromise from "@repo/utils/UPromise";

import UArray from "./UArray.mts";
import UFunction from "./UFunction.mts";
import UProxy from "./UProxy.mts";
import UString from "./UString.mts";
import UTuple from "./UTuple.mts";
import UType from "./UType.mts";

declare namespace Analytics {
  type Properties = {
    [key: string]: UArray.Maybe<
      string | number | boolean | undefined | Properties
    >;
  };

  type EventsByClientType = Record<
    Analytics.Client.Type,
    Record<string, Analytics.Properties>
  >;

  type Client<
    Events extends Record<string, Analytics.Properties> = any,
    Context extends UObject.Any | undefined = any,
  > = {
    track(
      eventName: UType.Narrow<keyof Events, string>,
      properties: Analytics.Properties,
      context: Context
    ): void;
    identify(
      payload: {
        userId?: string;
        properties?: Record<string, any>;
      },
      context: Context
    ): void;
    anonymize?(): void;
  };

  namespace Client {
    type Type = "analytics" | "conversion" | "other";
    type Registration<
      A extends Analytics<any, any, any> | any,
      Type extends Client.Type,
    > = UPromise.Maybe<Registration.Sync<A, Type>>;
    namespace Registration {
      type Sync<
        A extends Analytics<any, any, any> | any,
        Type extends Client.Type,
      > =
        A extends Analytics<infer EventsByClientType, infer Context, any>
          ? Analytics.Client<EventsByClientType[Type], Context> | undefined
          : never;
    }
    type Internal = Promise<Analytics.Client | undefined> & {
      cleanups: (() => void)[];
    };
  }

  type EventProperties<
    A extends Analytics<any, any, any>,
    EventName extends A extends Analytics<infer Events, any, any>
      ? keyof Events["analytics"]
      : never,
  > =
    A extends Analytics<infer Events> ? Events["analytics"][EventName] : never;
}

class Analytics<
  EventsByClientType extends Analytics.EventsByClientType,
  Context extends UObject.Any = {},
  ClientName extends string = never,
> extends EventEmitter<
  [
    // TODO: Fix configurable-eventemitter emit.withContext type
    [
      "track",
      [
        name: UType.Narrow<keyof EventsByClientType["analytics"], string>,
        properties: Analytics.Properties,
        context: Context,
      ],
    ],
    [
      "convert",
      [
        name: UType.Narrow<keyof EventsByClientType["conversion"], string>,
        properties: Analytics.Properties,
        context: Context,
      ],
    ],
    [
      "identify",
      [
        payload: {
          userId?: string;
          properties?: Analytics.Properties;
          authorized?: boolean;
        },
        context: Context,
      ],
    ],
    ["anonymize"],
  ]
> {
  private authorizationPromise?: Promise<boolean>;

  clientsByType = {
    analytics: {} as Record<ClientName, Analytics.Client.Internal>,
    conversion: {} as Record<ClientName, Analytics.Client.Internal>,
    other: {} as Record<ClientName, Analytics.Client.Internal>,
  };

  context = {} as Context;

  baseProperties = () => ({}) as Analytics.Properties;

  constructor(public options = { logging: true }) {
    super();
    // @ts-expect-error TODO: Fix configurable-eventemitter middleware types
    this.use((event, data) => {
      if (event === "track")
        return [
          event,
          // @ts-expect-error
          // Spreading data[1] twice to make true properties appear earlier in
          // logs, but still override baseProperties if necessary
          [data[0], { ...data[1], ...this.baseProperties(), ...data[1] }],
        ];
      return [event, data];
    });
    this.on.all((name, data) => {
      this.log(name, [data].flat());
    });
  }

  private log(method: string, args: any[]) {
    if (this.options.logging)
      Log.withTrace`${Log.fn([Log.color("green", "Analytics"), method], args)}`;
  }

  authorize(fn: () => UPromise.Maybe<boolean>) {
    this.authorizationPromise = Promise.resolve(fn());
    return this;
  }

  setBaseProperties(baseProperties: typeof this.baseProperties) {
    this.baseProperties = baseProperties;
    return this;
  }

  track<
    Name extends UType.Narrow<keyof EventsByClientType["analytics"], string>,
  >(
    eventName: Name,
    ...[properties]: {} extends EventsByClientType["analytics"][Name]
      ? [properties?: EventsByClientType["analytics"][Name]]
      : [properties: EventsByClientType["analytics"][Name]]
  ) {
    this.emit.cacheUntil(this.authorizationPromise || Promise.resolve())(
      "track",
      [eventName, properties || {}, this.context]
    );
  }

  convert<
    Name extends UType.Narrow<keyof EventsByClientType["conversion"], string>,
  >(
    eventName: Name,
    ...[properties]: {} extends EventsByClientType["conversion"][Name]
      ? [properties?: EventsByClientType["conversion"][Name]]
      : [properties: EventsByClientType["conversion"][Name]]
  ) {
    this.emit.cacheUntil(this.authorizationPromise || Promise.resolve())(
      "convert",
      [eventName, properties || {}, this.context]
    );
  }

  identify(properties: Analytics.Properties): void;

  identify(id: string, properties?: Analytics.Properties): void;

  identify(
    idOrProperties: string | Analytics.Properties,
    properties?: Analytics.Properties
  ) {
    this.emit.cacheUntil(this.authorizationPromise || Promise.resolve())(
      "identify",
      [
        typeof idOrProperties === "string" || !idOrProperties
          ? { userId: idOrProperties, properties }
          : { properties: idOrProperties },
        this.context,
      ]
    );
  }

  anonymize() {
    this.emit.cacheUntil(this.authorizationPromise || Promise.resolve())(
      "anonymize"
    );
  }

  addClient<Type extends keyof typeof this.clientsByType, Name extends string>(
    type: Type,
    name: Name,
    createClient: (...args: any[]) => Analytics.Client.Registration<this, Type>
  ): Analytics<EventsByClientType, Context, ClientName | Name> {
    const typeClients = this.clientsByType[type as Analytics.Client.Type];

    (this.authorizationPromise || Promise.resolve(true)).then((authorized) => {
      if (!authorized) return;

      // Allow overrides: destroy existing client if present
      if (name in typeClients) {
        for (const cleanup of typeClients[name as unknown as ClientName]
          .cleanups) {
          cleanup();
        }
      }

      const client = (typeClients[name as unknown as ClientName] =
        Object.assign(Promise.resolve(createClient()), {
          cleanups: [] as (() => void)[],
        }));
      const safeClientPromise = client.catch(UFunction.noop);

      if (type === "analytics") {
        const { off } = this.on("track", async (args) => {
          const client = await safeClientPromise;
          if (!client) return;

          client.track(...args);
        });
        client.cleanups.push(off);
        client.catch(off);
      }
      {
        const { off } = this.on("convert", async ([name, ...rest]) => {
          const client = await safeClientPromise;
          if (!client) return;

          client.track(
            // @ts-expect-error `conversion: *` events are not explicitly defined
            type === "conversion" ? name : `conversion: ${name}`,
            ...rest
          );
        });
        client.cleanups.push(off);
        client.catch(off);
      }
      {
        const { off } = this.on("identify", async (args) => {
          const client = await safeClientPromise;
          if (!client) return;

          client.identify(...args);
        });
        client.cleanups.push(off);
        client.catch(off);
      }
      {
        const { off } = this.on("anonymize", async () => {
          const client = await safeClientPromise;
          if (!client) return;

          client.anonymize?.();
        });
        client.cleanups.push(off);
        client.catch(off);
      }
    });

    return this as any;
  }

  addClients<
    Clients extends {
      [Type in Analytics.Client.Type as `${Type}/${string}`]: (
        ...args: any[]
      ) => Analytics.Client.Registration<this, Type>;
    },
  >(
    clients: Clients
  ): Analytics<
    EventsByClientType,
    Context,
    | ClientName
    | UString.AfterPrefix<
        UType.Narrow<keyof Clients, string>,
        `${Analytics.Client.Type}/`
      >
  > {
    for (const [key, client] of Object.entries(clients)) {
      const [type, name] = UArray.assertLengthOf(
        UString.split(key as `${Analytics.Client.Type}/${string}`, "/"),
        2,
        ([, ...name]) =>
          `Client name "${name.join("/")}" may not have any "/" characters`
      );
      this.addClient(type, name, client);
    }
    return this as any;
  }

  only<Name extends ClientName>(...clientNames: Name[]) {
    const analytics = new Analytics<EventsByClientType, Context, Name>(
      this.options
    );
    analytics.authorizationPromise = this.authorizationPromise;
    analytics.context = this.context;
    for (const clientName of clientNames) {
      for (const type of Object.keys(this.clientsByType)) {
        if (clientName in this.clientsByType[type])
          analytics.addClient(
            type,
            clientName,
            () => this.clientsByType[type][clientName]
          );
      }
    }
    return analytics;
  }

  except<Name extends ClientName>(...clientNames: Name[]) {
    return this.only(
      ...(Object.values(this.clientsByType).flatMap((type) =>
        Object.keys(type).filter((key) => !clientNames.includes(key as Name))
      ) as ClientName[])
    ) as Analytics<EventsByClientType, Context, Exclude<ClientName, Name>>;
  }
}

export default Analytics;

export namespace StructuredEvents {
  export const formatted = Symbol("formatted");
  export const actions = Symbol("actions");

  export type ActionType =
    | "impression"
    // Maps to `: Tap`
    | "click"
    | "attempt"
    | "success"
    | "failure"
    | "debug";

  export type AsyncActions<P extends Analytics.Properties> = Record<
    "attempt" | "success" | "failure",
    P
  > & {
    failure: {
      errors: string[];
    };
  };

  export type Schema = {
    [key: string]: {
      [key: string]: Schema[string];
      [formatted]: string;
      [actions]?: {
        [A in ActionType]?: Analytics.Properties;
      };
    };
  };

  export type Flatten<E extends Schema> =
    // @ts-expect-error excessive type instantiation
    UObject.FromEntryUnion<FlattenImpl<E>>;

  type FlattenImpl<
    E extends Schema,
    Parents extends string[] = [],
  > = UObject.Value<{
    [K in Exclude<keyof E, symbol>]:
      | (E[K] extends { readonly [actions]: infer Actions }
          ? UObject.Entry<{
              [A in keyof Actions as `${UTuple.Join<
                [...Parents, E[K][typeof formatted]],
                " - "
              >}: ${UString.Capitalize<UType.Narrow<A, string>>}`]: Actions[A];
            }>
          : never)
      | FlattenImpl<E[K], [...Parents, E[K][typeof formatted]]>;
  }>;

  type Track<E extends Schema> = {
    [K in keyof E]: (E[K] extends {
      readonly [actions]: infer Actions;
    }
      ? <Action extends Extract<keyof Actions, ActionType>>(
          action: Action,
          ...[properties]: UObject.RequiredKeys<
            UType.Narrow<Actions[Action], Analytics.Properties>
          > extends never
            ? [(undefined | Actions[Action])?]
            : [Actions[Action]]
        ) => void
      : {}) &
      Track<Omit<E[K], symbol>>;
  };

  export const createTrackProxy = <Events extends Schema>(
    analytics: Analytics<any, any, any>,
    events: Events,
    {
      transform = ((...args) => args) as (
        name: string,
        properties: Analytics.Properties
      ) => [string, Analytics.Properties],
    } = {}
  ) =>
    UProxy.createPathProxy((path) => (action, properties) => {
      let current = events;
      analytics.track(
        ...transform(
          // @ts-expect-error
          `${path.map((segment) => (current = current[segment])[formatted]).join(" - ")}: ${UString.capitalize(action)}`,
          properties
        )
      );
    }) as unknown as Track<Events>;
}

export let shared: Analytics<any, any, any> | undefined;

export function setShared<A extends Analytics<any, any, any>>(client: A) {
  return (shared = client);
}
